import React, { useEffect, useContext, useState } from "react";
import { AppStateContext } from "../../../state/AppProvider";
import {
    deleteCustomAssistant,
    leaveAssistant,
    historyList,
    historyCount,
    disableAssistant
} from "../../../api/api";
import { Assistant, ChatHistoryLoadingState } from "../../../api/models";
import { AssistantEditor } from "./AssistantEditor";
import { AssistantViewer } from "./AssistantViewer";
import { AssistantTable } from "../ui/AssistantTable";
import { ConfirmationDialog } from "../ui/ConfirmationDialog";
import { useAssistantsRefresh } from "../hooks/useAssistantsRefresh";

interface AssistantManagerProps {
    onCancel: () => void;
    onNewClick: () => void;
    setOnNewClick: React.Dispatch<React.SetStateAction<() => void>>;
}

export const AssistantManager = ({ onCancel, setOnNewClick, onNewClick }: AssistantManagerProps) => {
    const appStateContext = useContext(AppStateContext);

    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [leaveId, setLeaveId] = useState<string | null>(null);
    const [confirmText, setConfirmText] = useState("");
    const [isDeletingAssistant, setIsDeletingAssistant] = useState(false);
    const [isLeavingAssistant, setIsLeavingAssistant] = useState(false);

    const [myAssistants, setMyAssistants] = useState<Assistant[]>([]);
    const [sharedAssistants, setSharedAssistants] = useState<Assistant[]>([]);
    const [jtcAssistants, setJtcAssistants] = useState<Assistant[]>([]);

    const [isEditing, setIsEditing] = useState(false);
    const [isViewing, setIsViewing] = useState(false);
    const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null);
    const user_groups = appStateContext?.state.userAuthClaims?.groups || [];

    useEffect(() => {
        const fetchAssistants = () => {
            const myCustom = appStateContext?.state.customAssistants || [];
            const shared = appStateContext?.state.sharedAssistants || [];
            const jtc = appStateContext?.state.jtcAssistants || [];

            setMyAssistants(myCustom);
            setSharedAssistants(shared);
            setJtcAssistants(jtc);
        };
        fetchAssistants();
    }, [appStateContext]);

    const combinedSharedAssistants = [
        ...jtcAssistants.map(a => ({ ...a, isJtc: true })),
        ...sharedAssistants.map(a => ({ ...a, isJtc: false })),
    ];

    // Deletion is confirmed by typing the assistant's title.
    const deleteTargetTitle = myAssistants.find(a => a.id === deleteId)?.title ?? "";

    // Refresh list after deleting/leaving
    const refreshAssistantsList = useAssistantsRefresh({ updateActiveAssistant: true });

    const refreshHistoryList = async (
        appStateContext: NonNullable<React.ContextType<typeof AppStateContext>>
    ): Promise<void> => {
        try {
            const count = await historyCount();
            if (count) {
                appStateContext.dispatch({ type: "SET_HISTORY_COUNT", payload: count });
            } else {
                appStateContext.dispatch({ type: "SET_HISTORY_COUNT", payload: 0 });
            }

            const chatHistory = await historyList();
            if (chatHistory) {
                appStateContext.dispatch({ type: "FETCH_CHAT_HISTORY", payload: chatHistory });
                appStateContext.dispatch({
                    type: "SET_HISTORY_CURRENT_COUNT",
                    payload: chatHistory.length
                });
            } else {
                appStateContext.dispatch({ type: "FETCH_CHAT_HISTORY", payload: null });
            }
        } catch (error) {
            console.error("There was an issue fetching chat history: ", error);

            appStateContext.dispatch({
                type: "UPDATE_CHAT_HISTORY_LOADING_STATE",
                payload: ChatHistoryLoadingState.Fail
            });
            appStateContext.dispatch({ type: "FETCH_CHAT_HISTORY", payload: null });
        }
    };

    const handleDelete = (id: string) => {
        setDeleteId(id);
        setConfirmText("");
        setIsDeletingAssistant(false);
    };

    const confirmDelete = async () => {
        if (deleteId && deleteTargetTitle && confirmText === deleteTargetTitle) {
            if (appStateContext?.state.assistant?.id === deleteId)
                onNewClick();
            appStateContext?.dispatch({
                type: "UPDATE_ASSISTANT",
                payload: jtcAssistants.filter((assistant) => assistant.assistant === "gpt4")[0]
            });
            setIsDeletingAssistant(true);
            try {
                await disableAssistant(deleteId);
                await deleteCustomAssistant(deleteId);
                await refreshHistoryList(appStateContext!);
                await refreshAssistantsList();
                if (appStateContext?.state.assistant?.id === deleteId)
                    onNewClick();
            } catch (err) {
                console.error(`Error deleting assistant with id ${deleteId}.`, err);
            } finally {
                setIsDeletingAssistant(false);
                setDeleteId(null);
            }
        }
    };

    const cancelDelete = () => {
        setDeleteId(null);
        setConfirmText("");
        setIsDeletingAssistant(false);
    };

    const handleLeave = (id: string) => {
        setLeaveId(id);
        setConfirmText("");
        setIsLeavingAssistant(false);
    };

    const confirmLeave = async () => {
        if (confirmText === "LEAVE" && leaveId) {
            setIsLeavingAssistant(true);
            if (appStateContext?.state.assistant?.id === leaveId)
                onNewClick();
            appStateContext?.dispatch({
                type: "UPDATE_ASSISTANT",
                payload: jtcAssistants.filter((assistant) => assistant.assistant === "gpt4")[0]
            });
            try {
                const response = await leaveAssistant(leaveId);
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error("Error leaving assistant:", errorText);
                } else {
                    await refreshHistoryList(appStateContext!);
                    await refreshAssistantsList();
                }
            } catch (err) {
                console.error(`Error leaving assistant with id ${leaveId}`, err);
            } finally {
                setIsLeavingAssistant(false);
                setLeaveId(null);
            }
        }
    };

    const cancelLeave = () => {
        setLeaveId(null);
        setConfirmText("");
        setIsLeavingAssistant(false);
    };

    const handleEdit = (id: string) => {
        setSelectedAssistantId(id);
        setIsEditing(true);
    };

    const handleView = (id: string) => {
        setSelectedAssistantId(id);
        setIsViewing(true);
    };

    return (
        <div className="relative flex flex-col h-full min-w-0">
            {isViewing && selectedAssistantId ? (
                <AssistantViewer
                    id={selectedAssistantId}
                    onCancel={() => {
                        setIsViewing(false);
                        setSelectedAssistantId(null);
                    }}
                />
            ) : isEditing && selectedAssistantId ? (
                <AssistantEditor
                    id={selectedAssistantId}
                    onSubmit={() => {
                        setIsEditing(false);
                        setSelectedAssistantId(null);
                    }}
                    onCancel={() => {
                        setIsEditing(false);
                        setSelectedAssistantId(null);
                    }}
                />
            ) : (
                <>
                    <div className="flex-grow min-w-0 overflow-y-auto overflow-x-hidden p-4">
                        {/* My Custom Assistants */}
                        <div>
                            <h4 className="text-lg font-semibold text-jtc-very-dark-grey mb-2">
                                My Custom Assistants
                            </h4>
                            {myAssistants.length > 0 ? (
                                <AssistantTable
                                    variant="owned"
                                    data={myAssistants}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                />
                            ) : (
                                <div className="p-6 rounded-md border-dashed border-gray-200 border text-center">
                                    <p className="text-gray-500 text-sm">
                                        {appStateContext?.state.canCreateAssistants
                                            ? "No assistants found, start by creating one!"
                                            : "You don't have any custom assistants yet. Assistants you manage will appear here."}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* JTC + Shared (JTC first), no actions for JTC, show "JTCGroup" as creator. */}
                        <div className="mt-8">
                            <h4 className="text-lg font-semibold text-jtc-very-dark-grey mb-2">
                                Assistants Shared With Me
                            </h4>
                            {combinedSharedAssistants.length > 0 ? (
                                <AssistantTable
                                    variant="shared"
                                    data={combinedSharedAssistants}
                                    userGroups={user_groups}
                                    onView={handleView}
                                    onLeave={handleLeave}
                                />
                            ) : (
                                <div className="p-6 rounded-md border-dashed border-gray-200 border text-center">
                                    <p className="text-gray-500 text-sm">
                                        No assistants have been shared with you.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer with Close */}
                    <div className="border-t border-gray-200 p-4 bg-white">
                        <div className="flex flex-row sm:flex-row-reverse gap-4">
                            <button className="jtc-secondary-button" onClick={onCancel}>
                                Close
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Delete Modal */}
            <ConfirmationDialog
                open={!!deleteId}
                title="Confirm Deletion"
                body={
                    <>
                        This action is permanent and cannot be undone. All conversations with this
                        assistant will also be lost.
                        <br />
                        Type <strong className="break-words">{deleteTargetTitle}</strong> to proceed.
                    </>
                }
                confirmLabel="DELETE"
                requiredText={deleteTargetTitle}
                busy={isDeletingAssistant}
                confirmText={confirmText}
                setConfirmText={setConfirmText}
                onConfirm={confirmDelete}
                onClose={cancelDelete}
                cancelText="CLOSE"
            />

            {/* Leave Modal */}
            <ConfirmationDialog
                open={!!leaveId}
                title="Confirm Leaving"
                body={
                    <>
                        Are you sure you want to leave this assistant? You will lose access to it
                        and your conversations. The creator can still re-add you at a future date.
                        <br />
                        Type <strong>LEAVE</strong> to proceed.
                    </>
                }
                confirmLabel="LEAVE"
                requiredText="LEAVE"
                busy={isLeavingAssistant}
                confirmText={confirmText}
                setConfirmText={setConfirmText}
                onConfirm={confirmLeave}
                onClose={cancelLeave}
                cancelText="CANCEL"
            />
        </div>
    );
};
